const ONE_SIZE_VALUE_RE = /^(unique|taille unique|one size|tu|einheitsgr(?:öße|oesse)|talla unica|taglia unica|maat uniek)$/i;

function isOneSizeOnly(inputs) {
  return inputs.length === 1 && ONE_SIZE_VALUE_RE.test(String(inputs[0]?.value || '').replace(/\s+/g, ' ').trim());
}

// Les tailles de la maison, dans l'ordre, toujours toutes affichées (demande du
// 13/10/2026). L'ordre des variantes sur Shopify suit leur création (« M, S » sur
// une fiche) : on ne s'y fie pas.
const HOUSE_SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'];

function sizeKey(value) {
  const v = String(value || '').replace(/\s+/g, '').toUpperCase();
  if (v === 'XXL') return '2XL';
  if (v === 'XXXL') return '3XL';
  return v;
}

/**
 * Sélecteur taille LV (cp-size) — initialisation réutilisable (fiche produit + quick-add).
 * @param {ParentNode} [root=document]
 * @returns {boolean}
 */
export function initCpSizePicker(root = document) {
  const SIZE_RE = /(taille|size|pointure|sizing|größe|grösse|talla|taglia|maat|مقاس)/i;

  function isSizeFieldset(fs) {
    if (!fs?.classList?.contains('variant-option--buttons')) return false;
    const legend = fs.querySelector('legend');
    return !!legend && SIZE_RE.test(legend.textContent || '');
  }

  const comp = root.querySelector('dialog-component.cp-size');
  if (!comp || comp.dataset.cpInit) return true;

  const vp =
    root.querySelector('.product-details variant-picker') || root.querySelector('variant-picker');
  if (!vp) return false;

  const current = comp.querySelector('[data-cp-size-current]');
  const labelEl = comp.querySelector('[data-cp-size-label]');
  const grid = comp.querySelector('[data-cp-size-grid]');
  if (!grid || !labelEl) return false;

  const labelPrompt = labelEl.getAttribute('data-cp-label-prompt') || '';
  const labelSelected = labelEl.getAttribute('data-cp-label-selected') || '';

  function sizeFieldset() {
    const fieldsets = vp.querySelectorAll('fieldset.variant-option--buttons');
    for (const fs of fieldsets) {
      if (isSizeFieldset(fs)) return fs;
    }
    return null;
  }

  function sizeInputs() {
    const fs = sizeFieldset();
    if (!fs) return [];
    const all = Array.from(fs.querySelectorAll('input[type="radio"]'));
    // Une « Taille unique » restée à côté de vraies tailles (XS…5XL) n'est pas une
    // taille : on ne la propose pas. Seule, elle reste (accessoires).
    const real = all.filter((i) => !ONE_SIZE_VALUE_RE.test(String(i.value || '').replace(/\s+/g, ' ').trim()));
    return real.length ? real : all;
  }

  let userPicked = false;

  function selectedValue() {
    for (const inp of sizeInputs()) {
      if (inp.checked) return inp.value;
    }
    return null;
  }

  function inputFor(key) {
    return sizeInputs().find((i) => sizeKey(i.value) === key) || null;
  }

  // Grille XS → 5XL quand la fiche suit ce barème ; sinon (autre barème), ses
  // valeurs telles quelles, pour ne jamais cacher une taille vendue.
  function gridSizes() {
    const inputs = sizeInputs();
    if (inputs.length && inputs.every((i) => HOUSE_SIZES.includes(sizeKey(i.value)))) {
      return HOUSE_SIZES.map((key) => ({ key, label: key }));
    }
    return inputs.map((i) => ({ key: sizeKey(i.value), label: i.value }));
  }

  function build() {
    grid.innerHTML = '';
    for (const { key, label } of gridSizes()) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'cp-size__tile';
      btn.textContent = label;
      btn.dataset.size = key;
      const inp = inputFor(key);
      if (!inp) {
        // Taille sans variante sur la fiche : barrée comme une épuisée, mais
        // inerte, faute de variante à acheter ou à suivre (alerte Klaviyo).
        btn.classList.add('is-unavailable');
        btn.disabled = true;
        grid.appendChild(btn);
        continue;
      }
      if (inp.disabled || inp.getAttribute('aria-disabled') === 'true') {
        // Taille épuisée : barrée mais sélectionnable, pour que la cliente
        // puisse s'abonner à l'alerte retour en stock de CETTE taille
        // (Horizon désactive l'achat et buy-buttons affiche « M'alerter »).
        btn.classList.add('is-unavailable');
      }
      btn.addEventListener('click', () => {
        // Relu au clic : le sélecteur natif peut avoir été re-rendu entre-temps.
        const target = inputFor(key);
        if (!target) return;
        userPicked = true;
        const targetLabel = target.closest('label');
        if (targetLabel) targetLabel.click();
        else target.click();
        sync();
        try {
          comp.closeDialog();
        } catch {
          const d = comp.querySelector('dialog');
          if (d) d.close();
        }
      });
      grid.appendChild(btn);
    }
  }

  function sync() {
    const oneSizeOnly = isOneSizeOnly(sizeInputs());
    const v = userPicked || oneSizeOnly ? selectedValue() : null;
    labelEl.textContent = v ? labelSelected : labelPrompt;
    if (current) current.textContent = v || '';
    const row = comp.querySelector('.cp-size__row');
    if (v) {
      row?.classList.remove('cp-size__row--error');
      row?.removeAttribute('aria-invalid');
    }
    for (const t of grid.querySelectorAll('.cp-size__tile')) {
      t.classList.toggle('is-selected', (userPicked || oneSizeOnly) && !!inputFor(t.dataset.size)?.checked);
    }
  }

  function deselectAll() {
    for (const i of sizeInputs()) i.checked = false;
  }

  if (!sizeInputs().length) return false;

  // « Guide des tailles » de la carte conseil (cp-conseil-taille) : sur la fiche
  // produit, on referme le sélecteur puis on ouvre le panneau du guide
  // (size-guide-trigger) ; ailleurs
  // (quick-add d'une collection, panneau absent), le lien mène à la page guide.
  // Le guide refermé, on revient au sélecteur (demande du 13/10/2026).
  const guideLink = comp.querySelector('[data-cp-size-guide]');
  guideLink?.addEventListener('click', async (e) => {
    if (comp.closest('#quick-add-modal-content')) return;
    const guide = document.querySelector('dialog-component.size-guide-link');
    if (!guide || typeof guide.showDialog !== 'function') return;
    e.preventDefault();
    // Attendre la fin de la fermeture : elle rend le verrou de scroll du body,
    // que showDialog() reprend aussitôt pour le guide.
    try {
      await comp.closeDialog();
    } catch {
      comp.querySelector('dialog')?.close();
    }
    // dialog:close part une fois la fermeture finie et le verrou de scroll rendu
    guide.addEventListener('dialog:close', () => comp.showDialog(), { once: true });
    guide.showDialog();
  });

  build();
  if (!isOneSizeOnly(sizeInputs())) deselectAll();
  sync();
  vp.addEventListener('change', sync);

  // Garde « choisir la taille avant d'ajouter » : un seul listener global,
  // conscient du scope (fiche produit OU modale quick-add). Lit l'état réel du
  // DOM pour ne JAMAIS bloquer le bouton d'une autre instance (bug : le listener
  // de la fiche bloquait l'ajout dans la modale des recommandations).
  installAddToCartGuard();

  comp.dataset.cpInit = '1';
  // Marque le drawer comme réellement fonctionnel : c'est cette classe qui
  // autorise le CSS à masquer le sélecteur natif. Sans elle (asset non chargé,
  // erreur module), le watchdog inline de cp-size-picker réaffiche le natif.
  comp.classList.add('cp-size--ready');
  return true;
}

let guardInstalled = false;

/**
 * Installe une seule fois le garde-fou « taille obligatoire » sur le document.
 * Détermine dynamiquement le scope du clic (modale quick-add vs fiche produit)
 * et ne bloque QUE si une taille existe et n'est pas sélectionnée DANS CE scope.
 */
function installAddToCartGuard() {
  if (guardInstalled) return;
  guardInstalled = true;

  const SIZE_RE = /(taille|size|pointure|sizing|größe|grösse|talla|taglia|maat|مقاس)/i;

  window.addEventListener(
    'click',
    (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      // Couvre aussi la barre d'ajout flottante (sticky-add-to-cart), qui
      // ajouterait sinon la première variante disponible sans choix de taille,
      // et le déclencheur d'alerte retour en stock, qui s'abonnerait sinon à la
      // variante par défaut sans que la cliente ait choisi sa taille.
      const ab = target.closest(
        '[data-testid="standalone-add-to-cart"], sticky-add-to-cart .add-to-cart-button, [data-bis-trigger]'
      );
      if (!ab) return;

      // Scope = la modale quick-add si le bouton y est, sinon la fiche produit.
      let scope = ab.closest('#quick-add-modal-content') || ab.closest('.product-information');
      if (!scope && ab.closest('sticky-add-to-cart')) {
        scope = document.querySelector('.product-information');
      }
      if (!scope) return;

      const comp = scope.querySelector('dialog-component.cp-size');
      if (!comp) return; // pas de sélecteur taille custom → ne rien bloquer

      const vp = scope.querySelector('.product-details variant-picker') || scope.querySelector('variant-picker');
      if (!vp) return;

      let hasSize = false;
      let picked = false;
      for (const fs of vp.querySelectorAll('fieldset.variant-option--buttons')) {
        const legend = fs.querySelector('legend');
        if (!legend || !SIZE_RE.test(legend.textContent || '')) continue;
        if (isOneSizeOnly(Array.from(fs.querySelectorAll('input[type="radio"]')))) continue;
        hasSize = true;
        if (fs.querySelector('input[type="radio"]:checked')) picked = true;
      }
      if (!hasSize || picked) return; // pas de taille à choisir, ou déjà choisie

      e.preventDefault();
      e.stopImmediatePropagation();

      const row = comp.querySelector('.cp-size__row');
      if (row) {
        try {
          row.scrollIntoView({ block: 'center', behavior: 'smooth' });
        } catch {
          /* ignore */
        }
        row.classList.add('cp-size__row--error');
        row.setAttribute('aria-invalid', 'true');
        row.classList.remove('cp-size__row--flash');
        void row.offsetWidth;
        row.classList.add('cp-size__row--flash');
        setTimeout(() => row.classList.remove('cp-size__row--flash'), 1400);
      }
    },
    true
  );
}

export function bootCpSizePicker(root = document) {
  if (initCpSizePicker(root) !== false) return;
  let tries = 0;
  const iv = setInterval(() => {
    tries++;
    if (initCpSizePicker(root) !== false || tries > 20) clearInterval(iv);
  }, 150);
}

if (typeof window !== 'undefined') {
  window.initCpSizePicker = initCpSizePicker;
  window.bootCpSizePicker = bootCpSizePicker;
}
